/**
 * Advertiser Dashboard - Cetalingua FM
 * Target path: /admin-panel/src/components/advertiser/AdvertiserDashboard.js
 * REPLACES previous version
 *
 * Changes from previous:
 * - REMOVED: MyListings import and "Listings (Legacy)" tab entirely
 * - REMOVED: Legacy listings pending count query
 * - REMOVED: 'listings' from valid tabs
 * - Kept: overview, profile, content, push-notifications, proof-upload, settings
 */

import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase-config';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import CompanyProfile from './CompanyProfile';
import WeeklyContentManager from './WeeklyContentManager';
import ProofUploadSection from './ProofUploadSection';
import PushNotificationForm from './PushNotificationForm';
import SpeciesReach from './SpeciesReach';
import { getSpeciesSummary } from '../../shared/speciesUtils';
import { getCadenceForTier } from '../../shared/advertiserConstants';
import './AdvertiserDashboard.css';

const AdvertiserDashboard = ({ user }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [contentStats, setContentStats] = useState({
    featuredMedia: 0,
    whatsNew: 0,
    hotDeals: 0,
    pending: 0,
    loading: true
  });
  
  const getInitialTab = () => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const validTabs = [
      'overview', 'profile', 'content',
      'push-notifications', 'proof-upload', 'settings'
    ];
    if (tab && validTabs.includes(tab)) {
      return tab;
    }
    return 'overview';
  };
  
  const [activeTab, setActiveTabState] = useState(getInitialTab);
  
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    const url = new URL(window.location);
    url.searchParams.set('tab', tab);
    window.history.replaceState({}, '', url);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchUserData();
    }
  }, [user]);

  // Fetch content stats — advertiserContent only, no legacy
  useEffect(() => {
    const fetchContentStats = async () => {
      if (!user?.uid) return;
      
      try {
        const contentQuery = query(
          collection(db, 'advertiserContent'),
          where('advertiserId', '==', user.uid)
        );
        const contentSnap = await getDocs(contentQuery);
        
        let featuredMedia = 0;
        let whatsNew = 0;
        let hotDeals = 0;
        let pendingCount = 0;
        
        contentSnap.forEach(doc => {
          const data = doc.data();
          if (data.status === 'approved' && data.isActive) {
            switch (data.type) {
              case 'featured_media': featuredMedia++; break;
              case 'whats_new': whatsNew++; break;
              case 'hot_deal': hotDeals++; break;
              default: break;
            }
          }
          if (data.status === 'pending') {
            pendingCount++;
          }
        });
        
        setContentStats({
          featuredMedia,
          whatsNew,
          hotDeals,
          pending: pendingCount,
          loading: false
        });
        
      } catch (error) {
        console.error('Error fetching content stats:', error);
        setContentStats(prev => ({ ...prev, loading: false }));
      }
    };
    
    fetchContentStats();
  }, [user]);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleProfileUpdate = async () => {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      setUserData(userDoc.data());
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="dashboard-error">
        <h2>Error Loading Dashboard</h2>
        <p>Unable to load your account information.</p>
        <button onClick={handleSignOut}>Sign Out</button>
      </div>
    );
  }

  // STAGE 3 (29 Sep 2026): access comes from users/{uid}.products (written only by the payment functions and by
  // admin grants such as the sticker program). "paid" = a Stripe subscription that includes Advertiser.
  const sub = userData.portalSubscription;
  const tier = ['active', 'trialing', 'past_due'].includes(sub?.status) && (sub?.products || []).includes('advertiser')
    ? 'paid' : 'free';
  const grantUntil = userData.accessGrant?.until?.toDate ? userData.accessGrant.until.toDate() : null;
  const accessEnds = tier === 'paid' ? (sub?.currentPeriodEnd ? new Date(sub.currentPeriodEnd) : null) : grantUntil;

  const isSubscriptionActive = () => (userData.products || []).includes('advertiser');

  const getSubscriptionBadge = () => {
    if (tier === 'paid') {
      return <span className="badge badge-paid">💰 Paid Advertiser</span>;
    }
    if (isSubscriptionActive()) {
      return <span className="badge badge-free">🎟️ Free (3 months)</span>;
    }
    if (userData.proofStatus === 'pending') {
      return <span className="badge badge-pending">⏳ Proof Pending</span>;
    }
    return <span className="badge badge-inactive">⚠️ Inactive</span>;
  };

  const cadence = getCadenceForTier(tier);
  const cadenceLabel = cadence === 'weekly' ? 'Weekly' : 'Monthly';

  return (
    <div className="advertiser-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1>🐋 Cetalingua FM</h1>
            <p className="company-name">{userData.companyName || 'Your Company'}</p>
          </div>
          <div className="header-right">
            {getSubscriptionBadge()}
            <span className="user-email">{user.email}</span>
            <button className="sign-out-btn" onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs — no more listings */}
      <nav className="dashboard-nav">
        <button
          className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button
          className={`nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          🏢 Company Profile
        </button>
        <button
          className={`nav-tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          📅 {cadenceLabel} Content
        </button>
        {tier === 'free' && (
          <button
            className={`nav-tab ${activeTab === 'proof-upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('proof-upload')}
          >
            🎟️ Free Program
          </button>
        )}
        {tier === 'paid' && (
          <button
            className={`nav-tab ${activeTab === 'push-notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('push-notifications')}
          >
            📱 Push Notifications
          </button>
        )}
        <button
          className={`nav-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Settings
        </button>
      </nav>

      {/* Main Content */}
      <main className="dashboard-main">
        
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {/* App users interested in your species (26 Oct 2026) */}
            <SpeciesReach />
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📸</div>
                <div className="stat-value">{contentStats.loading ? '...' : contentStats.featuredMedia}</div>
                <div className="stat-label">Featured Media</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📰</div>
                <div className="stat-value">{contentStats.loading ? '...' : contentStats.whatsNew}</div>
                <div className="stat-label">What's New</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🔥</div>
                <div className="stat-value">{contentStats.loading ? '...' : contentStats.hotDeals}</div>
                <div className="stat-label">Hot Deals</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">⏳</div>
                <div className="stat-value pending">{contentStats.loading ? '...' : contentStats.pending}</div>
                <div className="stat-label">Pending Approval</div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="quick-actions">
              <h2>Quick Actions</h2>
              <div className="action-grid">
                <button 
                  className="action-card"
                  onClick={() => setActiveTab('profile')}
                >
                  <div className="action-icon">🏢</div>
                  <div className="action-title">Edit Profile</div>
                  <div className="action-desc">Update company info & photos</div>
                </button>
                
                <button 
                  className={`action-card ${!isSubscriptionActive() ? 'disabled' : ''}`}
                  onClick={() => isSubscriptionActive() && setActiveTab('content')}
                  disabled={!isSubscriptionActive()}
                >
                  <div className="action-icon">📅</div>
                  <div className="action-title">Post {cadenceLabel} Update</div>
                  <div className="action-desc">
                    {isSubscriptionActive() 
                      ? 'Featured media, news, or deal'
                      : 'Activate subscription first'
                    }
                  </div>
                </button>
                
                <button 
                  className={`action-card ${tier !== 'paid' ? 'disabled' : ''}`}
                  onClick={() => tier === 'paid' && setActiveTab('push-notifications')}
                  disabled={tier !== 'paid'}
                >
                  <div className="action-icon">📱</div>
                  <div className="action-title">Send Notification</div>
                  <div className="action-desc">
                    {tier === 'paid'
                      ? 'Monthly push to whale enthusiasts'
                      : 'Paid advertisers only'
                    }
                  </div>
                </button>
              </div>
            </div>

            {/* Getting Started */}
            {tier === 'free' && !userData.proofStatus && (
              <div className="getting-started">
                <h2>🎯 Getting Started</h2>
                <div className="steps-container">
                  {(() => {
                    // Stage 3 (1 Oct 2026): stickers are mailed by us — show where the package is
                    const pr = userData.promoRequest;
                    const sent = pr?.status === 'sent';
                    const steps = [
                      { done: true, title: 'Account Created', text: "You've successfully created your account!" },
                      { done: sent, active: !sent, title: !pr ? 'Get Your Free Stickers' : sent ? 'Stickers Sent' : 'Stickers Requested',
                        text: !pr ? 'Tell us where to mail the free stickers and flyers'
                          : sent ? `Mailed${pr.sentAt?.toDate ? ` on ${pr.sentAt.toDate().toLocaleDateString()}` : ''} — check your mail`
                          : '⏳ Pending — we are preparing your package',
                        btn: !pr ? 'Request Stickers →' : null },
                      { active: sent, title: 'Upload Proof', text: 'Show the stickers or flyers in use', btn: sent ? 'Upload Proof →' : null },
                      { title: 'Get Approved', text: 'Admin approval within 24-48 hours' },
                      { title: 'Start Posting!', text: 'Create featured media, news, and deals' },
                    ];
                    return steps.map((st, i) => (
                      <div key={st.title} className={`step ${st.done ? 'completed' : st.active ? 'active' : ''}`} style={{ alignItems: 'center' }}>
                        <div className="step-number">{st.done ? '✓' : i + 1}</div>
                        <div className="step-content" style={{ flex: 1 }}>
                          <h3>{st.title}</h3>
                          <p style={{ marginBottom: st.btn ? 8 : 0 }}>{st.text}</p>
                          {st.btn && <button className="step-btn" onClick={() => setActiveTab('proof-upload')}>{st.btn}</button>}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Proof Status Notices */}
            {tier === 'free' && userData.proofStatus === 'pending' && (
              <div className="notice-card pending">
                <div className="notice-icon">⏳</div>
                <div className="notice-content">
                  <h3>Proof Under Review</h3>
                  <p>Your proof is being reviewed. You'll be notified within 24-48 hours.</p>
                </div>
              </div>
            )}

            {tier === 'free' && userData.proofStatus === 'approved' && (
              <div className="notice-card approved">
                <div className="notice-icon">✅</div>
                <div className="notice-content">
                  <h3>3-Month Free Subscription Active!</h3>
                  <p>Thank you for promoting Cetalingua FM! You can now post content.</p>
                  <button className="step-btn" onClick={() => setActiveTab('content')}>
                    Post Content →
                  </button>
                </div>
              </div>
            )}

            {tier === 'free' && userData.proofStatus === 'rejected' && (
              <div className="notice-card rejected">
                <div className="notice-icon">❌</div>
                <div className="notice-content">
                  <h3>Proof Needs Revision</h3>
                  <p><strong>Reason:</strong> {userData.proofRejectionReason || 'See details in Free Program tab'}</p>
                  <button className="step-btn" onClick={() => setActiveTab('proof-upload')}>
                    Upload New Proof →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Company Profile Tab */}
        {activeTab === 'profile' && (
          <CompanyProfile 
            userId={user.uid} 
            userData={userData} 
            onUpdate={handleProfileUpdate}
          />
        )}

        {/* Weekly/Monthly Content Tab */}
        {activeTab === 'content' && (
          <WeeklyContentManager userId={user.uid} userData={userData} />
        )}

        {/* Proof Upload Tab */}
        {activeTab === 'proof-upload' && (
          <ProofUploadSection userId={user.uid} userData={userData} />
        )}

        {/* Push Notifications Tab */}
        {activeTab === 'push-notifications' && tier === 'paid' && (
          <PushNotificationForm userData={userData} userId={user.uid} />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="settings-tab">
            <h2>Account Settings</h2>
            
            <div className="settings-section">
              <h3>Company Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Name / organisation:</label>
                  <span>{userData.companyName || 'Not set'}</span>
                </div>
                <div className="info-item">
                  <label>Email:</label>
                  <span>{userData.email}</span>
                </div>
                <div className="info-item">
                  <label>Location:</label>
                  <span>
                    {userData.location?.city 
                      ? `${userData.location.city}, ${userData.location.country}` 
                      : 'Not set'}
                  </span>
                </div>
                <div className="info-item">
                  <label>Species:</label>
                  <span>
                    {userData.species?.length 
                      ? getSpeciesSummary(userData.species, 4)
                      : 'Not set'}
                  </span>
                </div>
                <div className="info-item">
                  <label>Content Cadence:</label>
                  <span>{cadenceLabel} ({tier} tier)</span>
                </div>
              </div>
              <button 
                className="edit-profile-btn"
                onClick={() => setActiveTab('profile')}
              >
                Edit Company Profile →
              </button>
            </div>

            <div className="settings-section">
              <h3>Subscription Status</h3>
              <div className="subscription-info">
                <div className="info-item">
                  <label>Type:</label>
                  {getSubscriptionBadge()}
                </div>
                {accessEnds && (
                  <div className="info-item">
                    <label>{tier === 'paid' && !sub?.cancelAtPeriodEnd ? 'Renews:' : 'Expires:'}</label>
                    <span>
                      {accessEnds.toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="settings-section">
              <h3>Account Actions</h3>
              <button className="danger-btn" onClick={handleSignOut}>
                Sign Out
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdvertiserDashboard;
